import { getAuthHeaders, authedFetch } from './authService';
const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';

const notifyServiceError = (msg: string) => {
  try { window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } })); } catch (e) { /* ignore */ }
};

const throwIfFailed = (body: any, notifyOnError = true) => {
  if (body?.success === false) {
    const msg: string = (typeof body?.message === 'string' ? body.message : null) ?? 'Request failed';
    if (notifyOnError) notifyServiceError(msg);
    throw new Error(msg);
  }
};

export type CommandUserPayload = {
  name: string;
  phone?: string;
  email: string;
  role: 'SUPERVISOR' | 'DISPATCHER';
};

export async function createCommandUser(payload: CommandUserPayload): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-users/create`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

export async function getCommandUsers(page?: number, pageSize?: number, signal?: AbortSignal, search?: string, role?: string): Promise<{ command_users: any[]; meta?: any }> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  if (search) params.append('search', search);
  if (role) params.append('role', role);
  const q = params.toString();
  const url = `${HOSTNAME}/adminportal/command-users${q ? `?${q}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', signal, headers: { ...getAuthHeaders() } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const command_users = body?.data?.command_users ?? body?.command_users ?? body?.data ?? [];
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { command_users: Array.isArray(command_users) ? command_users : [], meta };
}

/**
 * Update a command user by id.
 * PATCH /adminportal/command-users/{user-id}/edit
 * payload should contain only fields to update
 */
export async function updateCommandUser(userId: string | number, payload: any): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-users/${userId}/edit`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

/**
 * Deactivate a command user by id.
 * POST /adminportal/command-users/{userId}/deactivate
 */
export async function deactivateCommandUser(userId: string | number): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-users/${userId}/deactivate`;
  const res = await authedFetch(url, { method: 'POST', headers: { ...getAuthHeaders() } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

/**
 * KAN-129: Set a command user's active state (activate or deactivate).
 * POST /adminportal/command-users/{userId}/active  body: { active }
 */
export async function setCommandUserActive(userId: string | number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-users/${userId}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

// ─── Command Centre Entity APIs ─────────────────────────────────────────────

export type CommandCentrePayload = {
  name: string;
  city?: string;
  state?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
};

export async function createCommandCentre(payload: CommandCentrePayload): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-centres/create`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

export async function getCommandCentres(page?: number, pageSize?: number, signal?: AbortSignal): Promise<{ command_centres: any[]; meta?: any }> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  const q = params.toString();
  const url = `${HOSTNAME}/adminportal/command-centres${q ? `?${q}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', signal, headers: { ...getAuthHeaders() } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const command_centres = body?.data?.command_centres ?? body?.command_centres ?? body?.data ?? [];
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { command_centres: Array.isArray(command_centres) ? command_centres : [], meta };
}

export async function updateCommandCentre(centreId: string | number, payload: Partial<CommandCentrePayload>): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-centres/${centreId}/edit`;
  const res = await authedFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

export async function deactivateCommandCentre(centreId: string | number): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-centres/${centreId}/deactivate`;
  const res = await authedFetch(url, { method: 'POST', headers: { ...getAuthHeaders() } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

/**
 * KAN-129: Set a command centre's active state (activate or deactivate).
 * POST /adminportal/command-centres/{centreId}/active  body: { active }
 */
export async function setCommandCentreActive(centreId: string | number, active: boolean): Promise<any> {
  const url = `${HOSTNAME}/adminportal/command-centres/${centreId}/active`;
  const res = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ active }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.message ?? body?.error ?? `Server returned ${res.status}`;
    const msg = typeof err === 'string' ? err : JSON.stringify(err);
    notifyServiceError(msg);
    throw new Error(msg);
  }
  throwIfFailed(body);
  return body;
}

export async function getCommandCentreStaff(centreId: string | number, page?: number, pageSize?: number, signal?: AbortSignal): Promise<{ command_users: any[]; meta?: any }> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  const q = params.toString();
  const url = `${HOSTNAME}/adminportal/command-centres/${centreId}/staff${q ? `?${q}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', signal, headers: { ...getAuthHeaders() } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    // fallback: return empty gracefully
    return { command_users: [], meta: null };
  }
  const command_users = body?.data?.command_users ?? body?.command_users ?? body?.data ?? [];
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { command_users: Array.isArray(command_users) ? command_users : [], meta };
}

export async function getAdminUsers(pageSize = 200): Promise<Array<{ user_id: string; name: string; email: string }>> {
  const url = `${HOSTNAME}/adminportal/admin-users?page_size=${pageSize}`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  const body = await res.json().catch(() => null);
  if (!res.ok) return [];
  return body?.data?.admin_users ?? body?.admin_users ?? [];
}

export default { createCommandUser, getCommandUsers, createCommandCentre, getCommandCentres, updateCommandCentre, deactivateCommandCentre, getAdminUsers };
